/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {getRoute, Route} from '@/routes/RouteDefinitions';

/**
 * Defines the navigation guard callback signature. The guard receives the
 * target url of an in-app route change and returns `true` to block the
 * route change, otherwise `false` to allow it.
 *
 * @param url The target url of the route change, including any query string.
 */
export type NavigationGuard = (url: string) => boolean;

/**
 * Provides a reference to the currently registered navigation guard, if any.
 */
let navigationGuard: NavigationGuard | undefined;

/**
 * Indicates if the browser's native unload prompt is skipped.
 */
let unloadPromptSkipped = false;

/**
 * Registers the navigation guard which is consulted before each in-app route
 * change. Only one guard can be active, so each call replaces the previously
 * registered guard. Call `clearNavigationGuard` during component cleanup.
 *
 * ```typescript
 * import {setNavigationGuard} from '@/routes/NavigationGuard';
 * ...
 *
 *   // block route changes while a form has unsaved changes ...
 *   setNavigationGuard(function(url) {
 *     return hasUnsavedChanges(url);
 *   });
 * ```
 *
 * @param guard The function which determines if a route change is blocked.
 */
export const setNavigationGuard = (guard: NavigationGuard): void => {
  navigationGuard = guard;
};

/**
 * Removes the currently registered navigation guard, if any, so that no
 * in-app route change is blocked.
 *
 * ```typescript
 * import {clearNavigationGuard} from '@/routes/NavigationGuard';
 * ...
 *
 *   // remove the navigation guard on cleanup ...
 *   clearNavigationGuard();
 * ```
 */
export const clearNavigationGuard = (): void => {
  navigationGuard = undefined;
};

/**
 * Determines if the route change to the given url is blocked by the currently
 * registered navigation guard. A route change to the current page is never
 * blocked, and no route change is blocked when no guard is registered.
 *
 * ```typescript
 * import {isNavigationBlocked} from '@/routes/NavigationGuard';
 * ...
 *
 *   // abort the route change when blocked ...
 *   if (isNavigationBlocked(url, currentPage)) {
 *     throw 'Route change blocked';
 *   }
 * ```
 *
 * @param   url The target url of the route change, including any query string.
 * @param   currentPage The route of the current page.
 * @returns `true` when the registered guard blocks the route change, otherwise
 *          `false`.
 */
export const isNavigationBlocked = (url: string, currentPage: string): boolean => (
  !!navigationGuard && getRoute(url as unknown as Route) !== currentPage && navigationGuard(url) === true
);

/**
 * Skips the browser's native unload prompt (reload, tab close) for the rest
 * of the page's life. Call it before an explicit user action, such as logout
 * or a session timeout, which leaves the page on purpose. The flag is never
 * reset, because the caller goes on to leave the page.
 *
 * ```typescript
 * import {skipUnloadPrompt} from '@/routes/NavigationGuard';
 * ...
 *
 *   // leave without the native prompt ...
 *   skipUnloadPrompt();
 * ```
 */
export const skipUnloadPrompt = (): void => {
  unloadPromptSkipped = true;
};

/**
 * Determines if the browser's native unload prompt is skipped.
 *
 * ```typescript
 * import {isUnloadPromptSkipped} from '@/routes/NavigationGuard';
 * ...
 *
 *   // only prompt when not skipped ...
 *   if (!isUnloadPromptSkipped()) {
 *     event.preventDefault();
 *   }
 * ```
 *
 * @returns `true` when `skipUnloadPrompt` was called, otherwise `false`.
 */
export const isUnloadPromptSkipped = (): boolean => unloadPromptSkipped;
